import React, { useState, useEffect } from 'react';
import { Form, Button, InputGroup } from 'react-bootstrap';
import { fetchWithCSRF } from '../../utils/csrfUtils';
import useUpdateRelevantGroceryListIngredientsOnPantryIngredientCRUD from '../../hooks/updateRelevantGroceryListIngredientsOnPantryIngredientCRUD';
import { useAuth } from '../../context/AuthContext';
import { getTodayLocalDateString, getLocalDateString } from '../../utils/dateUtils';
import { getIngredientImageUrl } from '../../utils/imageUtils';

const styles = {
    dropdownItem: `
        .dropdown-item {
            background-color: transparent;
        }
        .dropdown-item:hover {
            background-color: #52cd92 !important;
            color: white;
        }
    `
};

function PantryIngredientForm({ pantryId, appUserId, onSubmit, onGroceryListUpdate }) {
    const [ingredientId, setIngredientId] = useState('');
    const [quantity, setQuantity] = useState('');
    const [unitOfMeasurement, setUnitOfMeasurement] = useState('');
    const [purchaseDate, setPurchaseDate] = useState('');
    const [expirationDate, setExpirationDate] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [ingredients, setIngredients] = useState([]);
    const [unitOptions, setUnitOptions] = useState([]);
    const [selectedIngredient, setSelectedIngredient] = useState(null);
    const [selectedIngredientEstimatedShelfLife, setSelectedIngredientEstimatedShelfLife] = useState(null);
    const [showDropdown, setShowDropdown] = useState(false);
    const [validated, setValidated] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const { updateGroceryLists } = useUpdateRelevantGroceryListIngredientsOnPantryIngredientCRUD();
    const { sessionToken } = useAuth();


    // Set initial purchase date to today
    useEffect(() => {
        setPurchaseDate(getTodayLocalDateString());
    }, []); // Empty dependency array means this runs once when component mounts



    // Second useEffect for API calls that need the session token
    useEffect(() => {
        if (!sessionToken) return;

        const fetchData = async () => {
            try {
                // Fetch ingredients and unit options in parallel
                const [ingredientsResponse, unitOptionsResponse] = await Promise.all([
                    fetchWithCSRF('/api/v1/ingredient', {
                        method: 'GET',
                        headers: {
                            'Authorization': `Bearer ${sessionToken}`
                        }
                    }),
                    fetchWithCSRF('/api/v1/ingredient/unit_of_measurement_enum', {
                        method: 'GET',
                        headers: {
                            'Authorization': `Bearer ${sessionToken}`
                        }
                    })
                ]);

                // Handle ingredients response
                if (!ingredientsResponse.ok) {
                    throw new Error(`Failed to fetch ingredients: ${ingredientsResponse.status}`);
                }
                const ingredientsData = await ingredientsResponse.json();
                if (!Array.isArray(ingredientsData)) {
                    throw new Error('Invalid ingredients data format');
                }
                setIngredients(ingredientsData);

                // Handle unit options response
                if (!unitOptionsResponse.ok) {
                    throw new Error(`Failed to fetch unit options: ${unitOptionsResponse.status}`);
                }
                const unitOptionsData = await unitOptionsResponse.json();
                if (!Array.isArray(unitOptionsData)) {
                    throw new Error('Invalid unit options data format');
                }
                setUnitOptions(unitOptionsData.map(item => item.unit));

            } catch (error) {
                console.error('Error fetching data:', error);
                // You might want to set some error state here to show to the user
            }
        };

        fetchData();
    }, [sessionToken]);

    useEffect(() => {
        if (selectedIngredient) {
            const fetchSelectedIngredient = async () => {
                try {
                    const response = await fetchWithCSRF(`/api/v1/ingredient/${selectedIngredient.id}`, {
                        method: 'GET',
                        headers: {
                            'Authorization': `Bearer ${sessionToken}`
                        }
                    });
                    if (!response.ok) throw new Error('Failed to fetch selected ingredient');
                    const data = await response.json();
                    setQuantity('');
                    setUnitOfMeasurement(data[0].unit_of_measurement);
                    setSelectedIngredientEstimatedShelfLife(data[0].estimated_shelf_life);
                } catch (error) {
                    console.error('Error fetching selected ingredient:', error);
                }
            };
            fetchSelectedIngredient();
        }
    }, [selectedIngredient]);

    useEffect(() => {
        if (purchaseDate && selectedIngredientEstimatedShelfLife) {
            const newExpirationDate = new Date(purchaseDate + 'T12:00:00');
            newExpirationDate.setDate(newExpirationDate.getDate() + selectedIngredientEstimatedShelfLife);
            setExpirationDate(getLocalDateString(newExpirationDate));
        }
    }, [purchaseDate, selectedIngredientEstimatedShelfLife]);

    useEffect(() => {
        if (searchTerm === '') {
            setSelectedIngredient(null);
            setShowDropdown(false);
            setQuantity('');
            setUnitOfMeasurement('');
            setExpirationDate('');
        } else if (selectedIngredient && searchTerm !== selectedIngredient.name) {
            setSelectedIngredient(null);
            setShowDropdown(true);
            setQuantity('');
            setUnitOfMeasurement('');
            setExpirationDate('');
        } else {
            setShowDropdown(true);
        }
    }, [searchTerm, selectedIngredient]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setValidated(true);
        setErrorMessage('');

        if (!selectedIngredient) {
            setErrorMessage('Please select an ingredient');
            return;
        }

        if (!quantity || quantity <= 0) {
            setErrorMessage('Please enter a valid quantity');
            return;
        }

        if (purchaseDate) {
            const currentDate = new Date();
            const selectedPurchaseDate = new Date(purchaseDate);
            
            if (selectedPurchaseDate > currentDate) {
                setErrorMessage('Purchase date cannot be in the future.');
                return;
            }
        }

        // Calculate final dates for submission
        const finalPurchaseDate = purchaseDate || getTodayLocalDateString();
        let finalExpirationDate = expirationDate;
        
        if (!finalExpirationDate) {
            const newExpirationDate = new Date(finalPurchaseDate + 'T12:00:00');
            newExpirationDate.setDate(newExpirationDate.getDate() + selectedIngredientEstimatedShelfLife);
            finalExpirationDate = getLocalDateString(newExpirationDate);
        }

        try {
            if (!sessionToken) {
                throw new Error('No valid session found');
            }

            console.log("about to submit with purchase date: ", purchaseDate + " and expiration date: ", expirationDate);

            const response = await fetchWithCSRF('/api/v1/pantry-ingredient', {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${sessionToken}`
                },
                body: JSON.stringify({
                    pantry_id: pantryId,
                    ingredient_id: ingredientId,
                    quantity: quantity,
                    unit_of_measurement: unitOfMeasurement,
                    purchase_date: finalPurchaseDate,
                    expiration_date: finalExpirationDate,
                    app_user_id: appUserId,
                    is_confirmed: true,
                    confirmed_at: new Date().toISOString()
                }),
            });
            
            if (!response.ok) throw new Error('Failed to create pantry ingredient');
            console.log("pantry ingredient created successfully");
            
            const data = await response.json();

            // Update relevant grocery lists using hook
            const updateSuccess = await updateGroceryLists(ingredientId, appUserId, onGroceryListUpdate);
            if (!updateSuccess) {
                console.error('Failed to update grocery lists');
            }
            
            // Reset form state
            setSearchTerm('');
            setSelectedIngredient(null);
            setQuantity('');
            setUnitOfMeasurement('');
            setPurchaseDate('');
            setExpirationDate('');
            setValidated(false);
            
            // Call the onSubmit callback with the new data
            if (onSubmit) onSubmit(data);
        } catch (error) {
            console.error('Error creating pantry ingredient:', error);
            setErrorMessage('Failed to add ingredient to pantry');
        }
    };

    const filteredIngredients = ingredients.filter(ingredient =>
        ingredient.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="mb-4 p-0">
            <style>{styles.dropdownItem}</style>
            <Form onSubmit={handleSubmit} noValidate validated={validated} className="add-pantry-ingredient-form mx-auto">
                <div className="row">
                    {/* Ingredient Search */}
                    <div className="col-12 mb-2">
                        <Form.Group>
                            <div className="text-center mb-3">
                                {selectedIngredient && (
                                    <img 
                                        src={getIngredientImageUrl(selectedIngredient.id)}  
                                        className="ingredient-image" 
                                        style={{width: '75px', height: '75px'}}
                                    />
                                )}
                            </div>
                            <Form.Label>Select Ingredient</Form.Label>
                            <InputGroup hasValidation>
                                <Form.Control
                                    type="text"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    placeholder="Type to search..."
                                    autoComplete="off"
                                    isInvalid={validated && !selectedIngredient}
                                    className="form-control-custom"
                                    
                                />
                                {selectedIngredient && (
                                    <Button 
                                        variant="outline-secondary"
                                        onClick={() => {
                                            setSearchTerm('');
                                            setSelectedIngredient(null);
                                            setValidated(false);
                                            setErrorMessage('');
                                        }}
                                    >
                                        ×
                                    </Button>
                                )}
                                <Form.Control.Feedback type="invalid">
                                    Please select an ingredient
                                </Form.Control.Feedback>
                            </InputGroup>
                            {showDropdown && filteredIngredients.length > 0 && !selectedIngredient && (
                                <div className="border rounded mt-1 shadow-sm" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                    {filteredIngredients.map(ingredient => (
                                        <div
                                            key={ingredient.id}
                                            className="px-3 py-2 dropdown-item"
                                            onClick={() => {
                                                setIngredientId(ingredient.id);
                                                setSearchTerm(ingredient.name);
                                                setSelectedIngredient(ingredient);
                                                setShowDropdown(false);
                                            }}
                                            style={{ 
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                minHeight: '40px',
                                                transition: 'background-color 0.15s ease-in-out'
                                            }}
                                        >
                                            {ingredient.name}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </Form.Group>
                    </div>

                    {selectedIngredient && (
                        <>
                            {/* Quantity and Unit */}
                            <div className="col-md-6 mb-3">
                                <Form.Group>
                                    <Form.Label>Quantity</Form.Label>
                                    <Form.Control
                                        type="number"
                                        min="0"
                                        step="0.1"
                                        value={quantity}
                                        onChange={(e) => setQuantity(e.target.value)}
                                        required
                                        isInvalid={validated && (!quantity || quantity <= 0)}
                                        className="form-control-custom"
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        Please enter a valid quantity
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </div>
                            <div className="col-md-6 mb-3">
                                <Form.Group>
                                    <Form.Label>Unit</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={unitOfMeasurement}
                                        disabled
                                        className="bg-light"
                                    />
                                </Form.Group>
                            </div>

                            {/* Dates */}
                            <div className="col-md-12 mb-3">
                                <Form.Group>
                                    <Form.Label>Purchase Date</Form.Label>
                                    <Form.Control
                                        type="date"
                                        value={purchaseDate}
                                        onChange={(e) => setPurchaseDate(e.target.value)}
                                        max={getTodayLocalDateString()}
                                        // min={new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString().split('T')[0]}
                                        className="form-control-custom"
                                    />
                                </Form.Group>
                            </div>
                            <div className="col-md-12 mb-3">
                                <Form.Group>
                                    <Form.Label>Expiration Date</Form.Label>
                                    <Form.Control
                                        type="date"
                                        value={expirationDate}
                                        onChange={(e) => setExpirationDate(e.target.value)}
                                        className="form-control-custom"
                                    />
                                </Form.Group>
                            </div>

                            {/* Submit Button */}
                            <div className="col-12">
                                <Button 
                                    type="submit" 
                                    style={{ 
                                        backgroundColor: '#52cd92',
                                        borderColor: '#52cd92'
                                    }}
                                    className="w-100"
                                >
                                    Add to Pantry
                                </Button>
                            </div>
                        </>
                    )}
                </div>
            </Form>
        </div>
    );
}

export default PantryIngredientForm;